import Cookies from 'js-cookie';

import { isHttpLocation } from '../../router/routerUtils';

type CookieCategory = 'necessary' | 'preferences' | 'statistics' | 'marketing';

export type CookieConsent = Record<CookieCategory, boolean>;

export type CookiePreferences = {
	version: number;
	consent: CookieConsent;
	lastUpdated: string;
};

const CURRENT_PREFERENCES_VERSION = 1;
const PREFERENCES_STORAGE_KEY = 'semfi_prefs';

const COOKIE_DEFAULTS = {
	sameSite: 'lax' as const,
	secure: !isHttpLocation,
};

const categoryCookies: Record<CookieCategory, Record<string, unknown>> = {
	necessary: {
		// 1. ts_gotoken session cookie
		// 2. datadog cookie
	},
	preferences: {
		// 1. frontend cookie TS_GO_PRE_LOGIN_ROUTER_STATE is under preferences category
		// and set in when user access a route without been logged in
		// and set when the user clicks the 'Go back to login' button
	},
	statistics: {
		// add statistics cookies here
	},
	marketing: {
		// add marketing cookies here
	},
};

const defaultConsent: CookieConsent = {
	necessary: true,
	preferences: false,
	statistics: false,
	marketing: false,
};

export const getStoredConsent = (): CookieConsent => {
	const storedPrefs = localStorage.getItem(PREFERENCES_STORAGE_KEY);

	if (!storedPrefs) {
		return defaultConsent;
	}

	try {
		const prefs = JSON.parse(storedPrefs) as CookiePreferences;

		if (prefs.version < CURRENT_PREFERENCES_VERSION) {
			return defaultConsent;
		}

		return prefs.consent;
	} catch {
		return defaultConsent;
	}
};

const savePreferencesToStorage = (consent: CookieConsent): void => {
	const preferences: CookiePreferences = {
		version: CURRENT_PREFERENCES_VERSION,
		consent,
		lastUpdated: new Date().toISOString(),
	};
	globalThis.localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
};
const setCookie = (name: string, value: unknown, expires?: Date): void => {
	Cookies.set(name, typeof value === 'string' ? value : JSON.stringify(value), {
		...COOKIE_DEFAULTS,
		expires,
	});
};

const removeCookie = (name: string): void => {
	Cookies.remove(name, COOKIE_DEFAULTS);
};

export const updateCookiesBasedOnConsent = (consent: CookieConsent): void => {
	savePreferencesToStorage(consent);

	const oneYearFromNow = new Date();
	oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

	Object.entries(consent).forEach(([category, isAccepted]) => {
		const categoryKey = category as CookieCategory;
		if (isAccepted) {
			Object.entries(categoryCookies[categoryKey]).forEach(([cookieKey, cookieValue]) => {
				setCookie(cookieKey, cookieValue, oneYearFromNow);
			});
		} else {
			Object.keys(categoryCookies[categoryKey]).forEach(cookieKey => {
				removeCookie(cookieKey);
			});
		}
	});
};

const isPreferencesVersionOutdated = (): boolean => {
	const storedPrefs = localStorage.getItem(PREFERENCES_STORAGE_KEY);
	if (!storedPrefs) {
		return true;
	}

	try {
		const prefs = JSON.parse(storedPrefs) as CookiePreferences;
		return prefs.version < CURRENT_PREFERENCES_VERSION;
	} catch {
		return true;
	}
};

export const areCookiesSaved = (): boolean => {
	return !isPreferencesVersionOutdated();
};

export const acceptAllCookies = (): void => {
	const consent: CookieConsent = {
		necessary: true,
		preferences: true,
		statistics: true,
		marketing: true,
	};
	updateCookiesBasedOnConsent(consent);
};

export const declineAllCookies = (): void => {
	const consent: CookieConsent = {
		necessary: true,
		preferences: false,
		statistics: false,
		marketing: false,
	};
	updateCookiesBasedOnConsent(consent);
};
